import React, { type FC, type JSX } from 'react';
import { useTranslation } from '@/app/components/translations/Translation.tsx';
import { usePageWrapperData } from '@/app/common/pageWrapperProvider.tsx';
import { formatNumber } from '@/utils/numberUtils.ts';
import Stars from '@/app/common/components/Stars';
import TripAdvisorBubbles from '@/app/components/hotel-detail/trip-advisor/TripAdvisorBubbles';
import TripAdvisorOwl from '@/app/components/hotel-detail/trip-advisor/TripAdvisorOwl';
import TripAdvisorScore from '@/app/components/hotel-detail/trip-advisor/TripAdvisorScore';
import { isCardRatingVisible, isTripAdvisorSource } from '@/app/common/reviews/reviewSource';

import { RatingsContainer, ReviewsNumber, ReviewsTitle, Score } from './styled';
import type { DealCard } from '../../../../utils/buildDealCards/dealCardTypes';
import { decorateHotelRating } from '../../utils/getDecorators';

type RatingsProps = {
  dealCard: DealCard;
  isOverlay: boolean;
  showStars?: boolean;
};

const decorateRatings = (rating: number) => {
  const decimalRating = rating / 10;
  if (decimalRating === 10) return decimalRating;
  return decimalRating.toFixed(1);
};

const Ratings: FC<RatingsProps> = ({ dealCard, isOverlay, showStars = true }): JSX.Element | null => {
  const { localization } = usePageWrapperData();
  const { t } = useTranslation();
  const { locale } = localization;
  const { reviewsDisabled } = localization.configValuesApp;
  const isTripAdvisor = isTripAdvisorSource(dealCard.reviewSource);
  const {
    product: {
      accommodation: { stars, rating, reviewsNum },
      rate: {
        flags: { tsh },
      },
    },
  } = dealCard;
  const showRating = !reviewsDisabled && isCardRatingVisible(rating, dealCard.reviewSource);

  const hotelReviewClass = decorateHotelRating({
    n: rating,
    excellent: 'excellent',
    verygood: 'very-good',
    good: 'good',
    pleasant: 'pleasant',
    poor: 'poor',
  });
  const hotelReviewTitle = decorateHotelRating({
    n: rating,
    excellent: t('vacation.oss.filters.rating.excellent'),
    verygood: t('vacation.oss.filters.rating.verygood'),
    good: t('vacation.oss.filters.rating.good'),
    pleasant: t('vacation.oss.filters.rating.fair'),
    poor: t('vacation.oss.filters.rating.poor'),
  });
  const hotelReviewsLabel = t('vacation.oss.cards.reviews', {
    amount: `${reviewsNum ? formatNumber({ locale, number: reviewsNum }) : 1}`,
  });
  const hotelReviews = reviewsNum > 0 && !tsh ? `${isTripAdvisor ? '' : '· '}${hotelReviewsLabel}` : '';

  if (!stars && !rating) return null;

  return (
    <RatingsContainer isOverlay={isOverlay}>
      {showStars && stars ? <Stars stars={stars} black={true} style={{ marginRight: 8 }} /> : null}
      {showRating && (
        <>
          {isTripAdvisor ? (
            <>
              <TripAdvisorScore rawScore={rating} css="margin-right: 8px;" />
              <TripAdvisorOwl alt="TripAdvisor" style={{ marginRight: 8, width: 24, height: 'auto' }} />
              <TripAdvisorBubbles rawScore={rating} css="font-size: 14px; margin-right: 8px;" />
            </>
          ) : (
            <>
              <Score variant="Title6" className={hotelReviewClass}>
                {decorateRatings(rating)}
              </Score>
              {hotelReviewTitle && (
                <ReviewsTitle variant="BodySmall" color="textSecondary">
                  {hotelReviewTitle}&nbsp;
                </ReviewsTitle>
              )}
            </>
          )}
          {!isOverlay && (
            <ReviewsNumber variant="BodySmall" color="textSecondary">
              {hotelReviews}
            </ReviewsNumber>
          )}
        </>
      )}
    </RatingsContainer>
  );
};

export default Ratings;
